import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Buttons/Button";
import EmptyState from "../../components/EmptyState";
import Sidebar from "../../components/Sidebar";
import { Spinner } from "../../components/Spinner";
import { fetchNewsArticle } from "../../services/newsService/newsService";
import type { NewsArticleDetail } from "../../services/newsService/newsService.types";
import { logger } from "../../utils/logger";
import { CoursesPageContainer } from "../CoursesPage/CoursesPage.styles";
import ArticleRenderer from "./ArticleRenderer";
import {
  ArticleCover,
  ArticleDate,
  ArticleExcerpt,
  ArticleHeader,
  ArticleNumber,
  ArticleTitle,
  HeaderActions,
  LoadingSlot,
  NewsMain,
  NewsShell,
} from "./NewsPage.styles";

const NewsArticlePage: React.FC = () => {
  const { articleId = "" } = useParams();
  const id = Number(articleId);
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!id) {
      setError("Некорректная статья.");
      setLoading(false);
      return;
    }
    fetchNewsArticle(id)
      .then((data) => {
        if (!active) return;
        setArticle(data);
        window.dispatchEvent(new Event("news-status-changed"));
      })
      .catch((requestError) => {
        logger.error("Не удалось загрузить статью:", requestError);
        if (active) setError("Статья недоступна или была перемещена в архив.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  return (
    <CoursesPageContainer>
      <Sidebar />
      <NewsMain>
        <NewsShell>
          <Breadcrumbs
            items={[
              { label: "Новости", to: "/news" },
              { label: article?.title || "Статья" },
            ]}
          />

          {loading ? (
            <LoadingSlot aria-label="Загрузка статьи">
              <Spinner $size="2rem" />
            </LoadingSlot>
          ) : null}

          {!loading && error ? (
            <EmptyState
              title="Статья недоступна"
              description={error}
            />
          ) : null}

          {article ? (
            <>
              {article.can_manage ? (
                <HeaderActions>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate("/news/" + article.id + "/edit")}
                  >
                    Редактировать
                  </Button>
                </HeaderActions>
              ) : null}
              <ArticleHeader>
                <ArticleNumber>
                  {article.number ? "№ " + article.number + " · " : ""}
                  {article.display_date
                    ? new Date(article.display_date).toLocaleDateString("ru-RU")
                    : ""}
                </ArticleNumber>
                <ArticleTitle>{article.title}</ArticleTitle>
                {article.excerpt ? <ArticleExcerpt>{article.excerpt}</ArticleExcerpt> : null}
                {article.updated_at &&
                article.published_at &&
                article.updated_at > article.published_at ? (
                  <ArticleDate>
                    Обновлено {new Date(article.updated_at).toLocaleDateString("ru-RU")}
                  </ArticleDate>
                ) : null}
              </ArticleHeader>
              {article.cover ? <ArticleCover src={article.cover.url} alt="" /> : null}
              <ArticleRenderer
                content={article.content_json}
                mediaAssets={article.media_assets}
                videoAssets={article.video_assets}
              />
            </>
          ) : null}
        </NewsShell>
      </NewsMain>
    </CoursesPageContainer>
  );
};

export default NewsArticlePage;
